import { ListedInstrument } from '@eih-netbroker/api-instrument';
import { isSameDay, isWeekend, startOfDay } from 'date-fns';
import { useCallback } from 'react';
import { date, number, object, string } from 'yup';
import {
  ExpiryType,
  InventoryManagementMethod,
  NbSelectItemOption,
  OrderType,
  TradeType,
  TradingSession,
} from '../../types';
import { useNonTradingDaysCommon } from '../core';
import { orderTypesWhenPriceRequired, orderTypesWhenStopActivationPriceRequired } from '../order/orderConst';

export interface OrderFormData {
  instrument?: ListedInstrument | null;
  orderType?: OrderType;
  tradeType?: TradeType;
  quantity?: number;
  price?: number;
  stopActivationPrice?: number;
  expiryType?: ExpiryType;
  expiry?: Date;
  inventoryManagementMethod?: InventoryManagementMethod | null;
  tradingSession?: TradingSession;
}

export type NormalOrderSchemaParams = {
  getTradingSessionOptions: (
    instrument: ListedInstrument | null | undefined,
    orderType?: OrderType,
  ) => NbSelectItemOption<TradingSession>[];
};

export const useNormalOrderSchemaCommon = ({ getTradingSessionOptions }: NormalOrderSchemaParams) => {
  const { getNonTradingDaysByMic } = useNonTradingDaysCommon();
  const getNormalOrderSchema = useCallback(
    (order: OrderFormData) => {
      const nonOrderDays = order.instrument ? getNonTradingDaysByMic(order.instrument.mic) : [];
      const tradingSessionOptions = getTradingSessionOptions(order.instrument, order.orderType);
      return object().shape(
        {
          instrument: object().required(),
          orderType: string().required(),
          tradeType: string().required(),
          quantity: number().required().moreThan(0),
          price: number().when('orderType', orderType => {
            return orderTypesWhenPriceRequired.some(ot => orderType.includes(ot))
              ? number().required().moreThan(0)
              : number().notRequired();
          }),
          stopActivationPrice: number().when('orderType', orderType => {
            return orderTypesWhenStopActivationPriceRequired.some(ot => orderType.includes(ot))
              ? number().required('Aktiválási ár kitöltése kötelező').moreThan(0)
              : number().notRequired();
          }),
          expiryType: string().required(),
          expiry: date().when('expiryType', expiryType => {
            return expiryType.includes(ExpiryType.Gtd)
              ? date()
                  .required()
                  .min(startOfDay(new Date()))
                  .test({
                    test: (day: Date) => {
                      return !(nonOrderDays.some(d => isSameDay(new Date(d), day)) || isWeekend(day));
                    },
                  })
              : date().notRequired();
          }),
          inventoryManagementMethod: string().when('tradeType', tradeType => {
            return tradeType.includes(TradeType.Sell) ? string().required() : string().notRequired();
          }),
          tradingSession: string()
            .required()
            .test({
              test: (tradingSession: string) => {
                return tradingSessionOptions.some(ts => ts.id === tradingSession);
              },
            }),
        },
        [
          ['orderType', 'orderType'],
          ['expiryType', 'expiryType'],
          ['tradeType', 'tradeType'],
        ],
      );
    },

    [getNonTradingDaysByMic, getTradingSessionOptions],
  );

  return { getNormalOrderSchema };
};
